import { useCallback, useRef, useState } from 'react';

/** Pull image File objects out of a clipboard's items (for paste-to-attach). */
export function extractImageFiles(items: DataTransferItemList | null | undefined): File[] {
  if (!items) return [];
  const imgs: File[] = [];
  for (const it of Array.from(items)) {
    if (it.kind === 'file' && it.type.startsWith('image/')) {
      const f = it.getAsFile();
      if (f) imgs.push(f);
    }
  }
  return imgs;
}

export interface ImageDropZone {
  /** True while a file drag is hovering the zone. */
  isDragOver: boolean;
  /** Spread onto the drop-target element. */
  dragProps: {
    onDragEnter: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
  };
  /** Attach to the editor/textarea to support paste-to-attach. */
  onPaste: (e: React.ClipboardEvent) => void;
}

/**
 * Drag-drop + paste image attaching for a compose surface. `addFiles` receives
 * the dropped/pasted files. Uses an enter/leave depth counter so the drag-over
 * affordance stays steady while the cursor moves over child elements (the naive
 * `e.currentTarget === e.target` check flickers).
 */
export function useImageDropZone(addFiles: (files: FileList | File[]) => void): ImageDropZone {
  const [isDragOver, setIsDragOver] = useState(false);
  const depthRef = useRef(0);

  const hasFiles = (e: React.DragEvent) => !!e.dataTransfer?.types?.includes('Files');

  const onDragEnter = useCallback((e: React.DragEvent) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    depthRef.current += 1;
    setIsDragOver(true);
  }, []);

  const onDragOver = useCallback((e: React.DragEvent) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent) => {
    if (!hasFiles(e)) return;
    depthRef.current -= 1;
    if (depthRef.current <= 0) {
      depthRef.current = 0;
      setIsDragOver(false);
    }
  }, []);

  const onDrop = useCallback((e: React.DragEvent) => {
    depthRef.current = 0;
    setIsDragOver(false);
    if (e.dataTransfer?.files?.length) {
      e.preventDefault();
      addFiles(e.dataTransfer.files);
    }
  }, [addFiles]);

  const onPaste = useCallback((e: React.ClipboardEvent) => {
    const imgs = extractImageFiles(e.clipboardData?.items);
    if (imgs.length) {
      e.preventDefault();
      addFiles(imgs);
    }
  }, [addFiles]);

  return { isDragOver, dragProps: { onDragEnter, onDragOver, onDragLeave, onDrop }, onPaste };
}
